import { useField, UseFieldConfig } from "react-final-form";

import { FormControl, FormControlLabel, FormHelperText, styled, Switch, SwitchProps } from "@mui/material";

const StyledFormHelperText = styled(FormHelperText)(({ theme }) => ({
    marginTop: 0,
    color: theme.palette.emteria.action,
    "&.Mui-error": {
        color: theme.palette.emteria.error,
    },
}));
const StyledFormWarningText = styled(FormHelperText)(({ theme }) => ({
    marginTop: 0,
    color: theme.palette.emteria.warning,
}));
const StyledFormErrorText = styled(FormHelperText)(({ theme }) => ({
    marginTop: 0,
    color: theme.palette.emteria.error,
}));
const StyledFormControlLabel = styled(FormControlLabel)({
    marginBottom: 0,
    marginLeft: 0,
});
const StyledFormControl = styled(FormControl)({
    display: "inline",
});
const StyledSwitch = styled(Switch)(({ theme }) => ({
    "& .MuiSwitch-switchBase.Mui-checked": {
        color: theme.palette.emteria.action,
    },
    "& .MuiSwitch-colorSecondary.Mui-checked + .MuiSwitch-track": {
        backgroundColor: theme.palette.emteria.action,
    },
}));

interface Props extends SwitchProps {
    name: string;
    label?: string | React.ReactNode;
    helperText?: string;
    warningText?: string;
    errorText?: string;
    labelPlacement?: "bottom" | "end" | "start" | "top";
    config?: UseFieldConfig;
}

export const SwitchField = ({
    name,
    config,
    label = "",
    helperText,
    warningText,
    labelPlacement = "start",
    onChange,
    errorText,
    ...rest
}: Props) => {
    const { input, meta } = useField<boolean>(name, { ...config, type: "checkbox" });
    const error = meta.touched && meta.error;

    const handleChange: SwitchProps["onChange"] = (e, child) => {
        input.onChange(e);
        if (onChange) {
            onChange(e, child);
        }
    };

    return (
        <StyledFormControl error={error}>
            <StyledFormControlLabel
                {...{ label, labelPlacement }}
                control={<StyledSwitch {...input} {...rest} onChange={handleChange} />}
            />
            <StyledFormHelperText>{error || helperText}</StyledFormHelperText>
            {warningText && <StyledFormWarningText>{warningText}</StyledFormWarningText>}
            {errorText && <StyledFormErrorText>{errorText}</StyledFormErrorText>}
        </StyledFormControl>
    );
};

interface UncontrolledProps extends SwitchProps {
    label?: string | React.ReactNode;
    helperText?: string;
    warningText?: string;
    errorText?: string;
    labelPlacement?: "bottom" | "end" | "start" | "top";
    error?: boolean;
    value?: boolean;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

export const UncontrolledSwitch = ({
    label = "",
    helperText,
    warningText,
    labelPlacement = "start",
    onChange,
    errorText,
    error,
    value,
    ...rest
}: UncontrolledProps) => {
    return (
        <StyledFormControl error={error}>
            <StyledFormControlLabel
                label={label}
                labelPlacement={labelPlacement}
                control={<StyledSwitch checked={value} onChange={onChange} {...rest} />}
            />
            {helperText && <StyledFormHelperText>{helperText}</StyledFormHelperText>}
            {warningText && <StyledFormWarningText>{warningText}</StyledFormWarningText>}
            {errorText && <StyledFormErrorText>{errorText}</StyledFormErrorText>}
        </StyledFormControl>
    );
};
